import React, { useEffect, useMemo, useState } from "react";
import PermissionsService from "../../services/PermissionsService";
import CreateAccountModal from "./CreateAccountModal";

const ACCESS_LABELS = {
  "": "Brak",
  TEACHER: "Nauczyciel",
  OPERATOR: "Operator",
  ADMIN: "Administrator",
};

const emptyFilters = {
  text: "",
  accessLevel: "all",
  clientLink: "all",
  teacherLink: "all",
};

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function matchesLinkFilter(value, filter) {
  if (filter === "all") return true;
  if (filter === "linked") return Boolean(value);
  if (filter === "unlinked") return !value;
  return true;
}

function filterAccounts(accounts, filters) {
  const text = normalizeText(filters.text);
  return accounts
    .filter(account => !text || [
      account.email,
      account.firstName,
      account.lastName,
      account.displayName,
      account.clientDisplayName,
      account.teacherDisplayName,
    ].some(value => normalizeText(value).includes(text)))
    .filter(account => filters.accessLevel === "all" || (account.accessLevel || "") === filters.accessLevel)
    .filter(account => matchesLinkFilter(account.clientId, filters.clientLink))
    .filter(account => matchesLinkFilter(account.teacherId, filters.teacherLink));
}

function Permissions() {
  const [accounts, setAccounts] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [accountCandidates, setAccountCandidates] = useState({ clients: [], teachers: [] });
  const [accessLevels, setAccessLevels] = useState(["", "TEACHER", "OPERATOR", "ADMIN"]);
  const [drafts, setDrafts] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rowState, setRowState] = useState({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createError, setCreateError] = useState("");

  const applyPermissionsPayload = (payload) => {
    setAccounts(payload.accounts || []);
    setClientOptions(payload.clientOptions || []);
    setTeacherOptions(payload.teacherOptions || []);
    setAccountCandidates(payload.accountCandidates || { clients: [], teachers: [] });
    setAccessLevels(payload.accessLevels || ["", "TEACHER", "OPERATOR", "ADMIN"]);
    setLoadError("");
  };

  const loadPermissions = (showLoading) => {
    if (showLoading) setLoading(true);
    return PermissionsService.getAccountPermissions()
      .then(applyPermissionsPayload)
      .catch(error => setLoadError(error.message))
      .finally(() => {
        if (showLoading) setLoading(false);
      });
  };

  useEffect(() => {
    loadPermissions(true);
  }, []);

  const visibleAccounts = useMemo(() => filterAccounts(accounts, filters), [accounts, filters]);

  const getDraft = account => drafts[account.accountId] || {
    accessLevel: account.accessLevel || "",
    clientId: account.clientId || "",
    teacherId: account.teacherId || "",
  };

  const updateDraft = (account, field, value) => {
    setDrafts(current => ({
      ...current,
      [account.accountId]: {
        ...getDraft(account),
        [field]: value,
      },
    }));
    setRowState(current => ({
      ...current,
      [account.accountId]: { status: "", error: "" },
    }));
  };

  const save = (account) => {
    const draft = getDraft(account);
    if (draft.accessLevel === "TEACHER" && !draft.teacherId) {
      setRowState(current => ({
        ...current,
        [account.accountId]: {
          status: "",
          error: "Dostep nauczyciela wymaga powiazania z nauczycielem.",
        },
      }));
      return Promise.resolve();
    }

    setRowState(current => ({
      ...current,
      [account.accountId]: { status: "saving", error: "" },
    }));

    return PermissionsService.updateAccountPermissions(account.accountId, draft)
      .then((updated) => {
        setAccounts(current => current.map(item => (
          item.accountId === account.accountId ? updated : item
        )));
        setDrafts((current) => {
          const next = { ...current };
          delete next[account.accountId];
          return next;
        });
        setRowState(current => ({
          ...current,
          [account.accountId]: { status: "saved", error: "" },
        }));
      })
      .catch((error) => {
        setRowState(current => ({
          ...current,
          [account.accountId]: { status: "", error: error.message },
        }));
      });
  };

  const openCreateModal = () => {
    setCreateError("");
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateError("");
    setCreateModalOpen(false);
  };

  const createAccount = payload => PermissionsService.createAccountFromProfile(payload)
    .then(() => PermissionsService.getAccountPermissions())
    .then((permissionsPayload) => {
      applyPermissionsPayload(permissionsPayload);
      closeCreateModal();
    })
    .catch(error => setCreateError(error.message));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="manage-title mb-0">Uprawnienia uzytkownikow</h2>
        {!loading && !loadError && (
          <button type="button" className="btn manage-button manage-red-button" onClick={openCreateModal}>
            Utworz konto
          </button>
        )}
      </div>
      {loading && <div role="status">Ladowanie uprawnien...</div>}
      {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}
      {!loading && !loadError && (
        <>
          <div className="row mx-0 mb-3">
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-search">
                Szukaj
                <input
                  id="permissions-search"
                  className="form-control"
                  value={filters.text}
                  onChange={event => setFilters({ ...filters, text: event.target.value })}
                />
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-access-filter">
                Poziom dostepu
                <select
                  id="permissions-access-filter"
                  className="form-control"
                  value={filters.accessLevel}
                  onChange={event => setFilters({ ...filters, accessLevel: event.target.value })}
                >
                  <option value="all">Wszystkie</option>
                  {accessLevels.map(level => (
                    <option key={level || "none"} value={level}>{ACCESS_LABELS[level] || level}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-client-filter">
                Powiazanie z klientem
                <select
                  id="permissions-client-filter"
                  className="form-control"
                  value={filters.clientLink}
                  onChange={event => setFilters({ ...filters, clientLink: event.target.value })}
                >
                  <option value="all">Wszystkie</option>
                  <option value="linked">Powiazane</option>
                  <option value="unlinked">Bez klienta</option>
                </select>
              </label>
            </div>
            <div className="col-md-3 px-1">
              <label htmlFor="permissions-teacher-filter">
                Powiazanie z nauczycielem
                <select
                  id="permissions-teacher-filter"
                  className="form-control"
                  value={filters.teacherLink}
                  onChange={event => setFilters({ ...filters, teacherLink: event.target.value })}
                >
                  <option value="all">Wszystkie</option>
                  <option value="linked">Powiazane</option>
                  <option value="unlinked">Bez nauczyciela</option>
                </select>
              </label>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm manage-text">
              <thead>
                <tr>
                  <th>Konto</th>
                  <th>Klient</th>
                  <th>Nauczyciel</th>
                  <th>Dostep</th>
                  <th>Akcja</th>
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map((account) => {
                  const draft = getDraft(account);
                  const state = rowState[account.accountId] || {};
                  return (
                    <tr key={account.accountId} data-testid={`permissions-row-${account.accountId}`}>
                      <td>
                        <div>{account.email}</div>
                        <small>{account.displayName}</small>
                      </td>
                      <td>
                        <select
                          id={`client-${account.accountId}`}
                          aria-label="Klient"
                          className="form-control"
                          value={draft.clientId}
                          onChange={event => updateDraft(account, "clientId", event.target.value)}
                        >
                          <option value="">Brak</option>
                          {clientOptions.map(option => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          id={`teacher-${account.accountId}`}
                          aria-label="Nauczyciel"
                          className="form-control"
                          value={draft.teacherId}
                          onChange={event => updateDraft(account, "teacherId", event.target.value)}
                        >
                          <option value="">Brak</option>
                          {teacherOptions.map(option => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          id={`access-${account.accountId}`}
                          aria-label="Dostep"
                          className="form-control"
                          value={draft.accessLevel}
                          onChange={event => updateDraft(account, "accessLevel", event.target.value)}
                        >
                          {accessLevels.map(level => (
                            <option key={level || "none"} value={level}>{ACCESS_LABELS[level] || level}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn manage-button manage-red-button"
                          disabled={state.status === "saving"}
                          onClick={() => save(account)}
                        >
                          Zapisz
                        </button>
                        {state.status === "saved" && <div className="text-success">Zapisano</div>}
                        {state.error && <div className="text-danger" role="alert">{state.error}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {createModalOpen && (
        <CreateAccountModal
          accountCandidates={accountCandidates}
          error={createError}
          onClose={closeCreateModal}
          onCreate={createAccount}
        />
      )}
    </div>
  );
}

export default Permissions;
